/*
 * PetalHush signup (P-063c). Sends email signups straight to Klaviyo's client Subscriptions API
 * (POST https://a.klaviyo.com/client/subscriptions/?company_id=…, expects 202). No Klaviyo script, no third-party JS.
 *
 * Used by:
 *   snippets/ph-klaviyo-popup.liquid  (pop-up; rendered by sections/ph-signup-popup.liquid in the footer group)
 *   sections/ph-newsletter.liquid     (inline form; falls back to its native Shopify customer form if Klaviyo can't be reached)
 *
 * Markup contract: a wrapper [data-ph-signup] holding one <form> with an <input type=email>, a submit button,
 * an error box [data-ph-err] and a hidden success box [data-ph-done]. Wrapper data attributes:
 *   data-company, data-list, data-revision, data-custom-source, data-signup-source,
 *   data-country      ("United States" or empty; empty = no location sent),
 *   data-msg-invalid, data-msg-network, data-msg-optin, data-sending,
 *   data-geo="US"            (send data-country only if Shopify's geolocation says the visitor really is in this country),
 *   data-fallback="shopify"  (footer only: on a network/5xx error, submit the native Shopify customer form instead),
 *   data-design="true"       (theme editor: never sends, shows the success state).
 * Pop-up: [data-ph-pop] root with data-delay (s), data-scroll (%), data-days (re-show after dismiss), data-section-id,
 *   data-geo="US" (open only when /browsing_context_suggestions.json detects that country; fails closed).
 * Why the geo check: with only the US market active, Shopify serves every visitor as country US
 * (localization.country.iso_code == 'US' and Shopify.country = "US" were seen from Vietnam, 2026-10-16), so the Liquid
 * gate alone can't keep EU/other visitors out. Shopify's own geolocation endpoint (same origin, no third party) can.
 * Storage key "ph_signup" (localStorage, cookie fallback): "subscribed" or "dismissed:<ms>".
 * Events: document "ph:subscribed" {detail:{source}}.
 */
(function () {
  'use strict';
  if (window.__phSignup) { window.__phSignup.init(); return; }

  var KEY = 'ph_signup';
  var EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
  var reduced = function () { return !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches); };

  /* ---------------------------------------------------------------- storage (localStorage, cookie fallback) */
  function readState() {
    var v = '';
    try { v = localStorage.getItem(KEY) || ''; } catch (e) {}
    if (!v) {
      try {
        var m = document.cookie.match(/(?:^|;\s*)ph_signup=([^;]+)/);
        if (m) v = decodeURIComponent(m[1]);
      } catch (e) {}
    }
    return v;
  }
  function writeState(v, days) {
    try { localStorage.setItem(KEY, v); } catch (e) {}
    try {
      document.cookie = KEY + '=' + encodeURIComponent(v) + ';path=/;max-age=' + Math.round(days * 86400) +
        ';SameSite=Lax' + (location.protocol === 'https:' ? ';Secure' : '');
    } catch (e) {}
  }
  function suppressed(days) {
    var v = readState();
    if (v === 'subscribed') return true;
    if (v.indexOf('dismissed:') === 0) {
      var t = parseInt(v.slice(10), 10);
      if (t && Date.now() - t < days * 864e5) return true;
    }
    return false;
  }

  /* ---------------------------------------------------------------- visitor country (Shopify geolocation, same origin) */
  var geoP = null;
  function geoCountry() {
    if (geoP) return geoP;
    try { var c0 = sessionStorage.getItem('ph_geo'); if (c0) return (geoP = Promise.resolve(c0)); } catch (e) {}
    geoP = fetch('/browsing_context_suggestions.json', { credentials: 'same-origin', headers: { accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var c = String((j && j.detected_values && j.detected_values.country && j.detected_values.country.handle) || '').toUpperCase();
        if (c) { try { sessionStorage.setItem('ph_geo', c); } catch (e) {} }
        return c;
      })
      .catch(function () { return ''; });
    return geoP;
  }

  /* ---------------------------------------------------------------- Klaviyo */
  function payload(c, email) {
    var attrs = {
      email: email,
      properties: { signup_source: c.signupSource || '', signup_page: location.pathname }
    };
    if (c.country) attrs.location = { country: c.country };
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: c.customSource || '',
          profile: { data: { type: 'profile', attributes: attrs } }
        },
        relationships: { list: { data: { type: 'list', id: c.list } } }
      }
    };
  }
  function send(c, email) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(c.company), {
      method: 'POST',
      headers: { revision: c.revision || '2025-10-15', 'content-type': 'application/vnd.api+json' },
      body: JSON.stringify(payload(c, email))
    }).then(function (r) { return r.status; });
  }

  /* ---------------------------------------------------------------- forms (pop-up + footer), delegated */
  function parts(box) {
    var form = box.querySelector('form');
    return {
      form: form,
      input: form && form.querySelector('input[type=email]'),
      btn: form && form.querySelector('[type=submit]'),
      err: box.querySelector('[data-ph-err]'),
      done: box.querySelector('[data-ph-done]'),
      optin: form && form.querySelector('[data-ph-optin]')
    };
  }
  function showError(p, msg, field) {
    if (p.err) { p.err.textContent = ''; p.err.textContent = msg; }
    var f = field || p.input;
    if (f) { f.setAttribute('aria-invalid', 'true'); f.focus(); }
  }
  function clearError(p) {
    if (p.err) p.err.textContent = '';
    if (p.input) p.input.removeAttribute('aria-invalid');
    if (p.optin) p.optin.removeAttribute('aria-invalid');
  }
  function setBusy(p, box, on) {
    if (!p.btn) return;
    if (on) {
      p.btn.style.minWidth = p.btn.offsetWidth + 'px';       // label swap never changes the button width
      p.btn.__label = p.btn.textContent;
      if (box.dataset.sending) p.btn.textContent = box.dataset.sending;
      p.btn.disabled = true;
      p.btn.setAttribute('aria-busy', 'true');
    } else {
      if (p.btn.__label != null) p.btn.textContent = p.btn.__label;
      p.btn.disabled = false;
      p.btn.removeAttribute('aria-busy');
    }
  }
  function succeed(box, p) {
    writeState('subscribed', 730);
    box.classList.add('is-done');
    var area = box.querySelector('[data-ph-form-area]') || p.form;
    if (area) area.hidden = true;
    box.querySelectorAll('[data-ph-hide-on-done]').forEach(function (el) { el.hidden = true; });
    if (p.done) { p.done.hidden = false; p.done.focus({ preventScroll: true }); }
    try { document.dispatchEvent(new CustomEvent('ph:subscribed', { detail: { source: box.dataset.signupSource || '' } })); } catch (e) {}
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    var box = form && form.closest && form.closest('[data-ph-signup]');
    if (!box) return;
    e.preventDefault();
    var p = parts(box);
    if (!p.input || (p.btn && p.btn.disabled)) return;
    var email = (p.input.value || '').trim();
    clearError(p);
    if (!EMAIL.test(email)) { showError(p, box.dataset.msgInvalid || ''); return; }
    if (p.optin && !p.optin.checked) { showError(p, box.dataset.msgOptin || box.dataset.msgInvalid || '', p.optin); return; }
    p.input.value = email;
    if (box.dataset.design === 'true') { succeed(box, p); return; }   // theme editor: never send
    setBusy(p, box, true);
    var cfg = {
      company: box.dataset.company, list: box.dataset.list, revision: box.dataset.revision,
      customSource: box.dataset.customSource, signupSource: box.dataset.signupSource, country: box.dataset.country || ''
    };
    var geo = (box.dataset.geo || '').toUpperCase();
    (cfg.country && geo ? geoCountry() : Promise.resolve(null))
      .then(function (c) {
        if (c !== null && c !== geo) cfg.country = '';         // not really in the US: join the list without a location
        return send(cfg, email);
      })
      .then(function (status) {
        if (status >= 200 && status < 300) { succeed(box, p); return; }
        if (status >= 400 && status < 500 && status !== 429) { setBusy(p, box, false); showError(p, box.dataset.msgInvalid || ''); return; }
        throw new Error('klaviyo ' + status);
      })
      .catch(function () {
        if (box.dataset.fallback === 'shopify' && p.form.getAttribute('action')) {
          HTMLFormElement.prototype.submit.call(p.form);       // native Shopify customer form (contact[email], tag newsletter)
          return;
        }
        setBusy(p, box, false);
        showError(p, box.dataset.msgNetwork || box.dataset.msgInvalid || '');
      });
  }, false);

  document.addEventListener('input', function (e) {
    var box = e.target && e.target.closest && e.target.closest('[data-ph-signup]');
    if (box && e.target.getAttribute('aria-invalid') === 'true') clearError(parts(box));
  });

  /* ---------------------------------------------------------------- pop-up */
  var BLOCKED = /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/)?(?:cart|checkouts?|account|policies|pages\/in-memory)(?:\/|$)/i;

  function otherLayerOpen(dlg) {
    if (document.documentElement.classList.contains('ph-lock') || document.querySelector('[aria-modal="true"].is-open:not(.ph-pop)')) return true;
    var open = document.querySelectorAll('dialog[open], details[open][scroll-lock], details[open].menu-drawer-container');
    for (var i = 0; i < open.length; i++) if (open[i] !== dlg) return true;
    return false;
  }
  function typing(root) {
    var a = document.activeElement;
    return !!(a && !root.contains(a) && a.matches && a.matches('input:not([type=button]):not([type=submit]):not([type=checkbox]):not([type=radio]), textarea, select, [contenteditable=""], [contenteditable=true]'));
  }
  function focusables(scope) {
    return Array.prototype.filter.call(
      scope.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.getClientRects().length > 0 && !el.closest('[hidden]'); }
    );
  }

  function initPop(root) {
    if (root.__phReady) return;
    root.__phReady = true;
    var dlg = root.querySelector('.ph-pop');
    var card = root.querySelector('.ph-pop-card');
    if (!dlg || !card) return;
    var d = root.dataset;
    var delay = Math.max(0, parseFloat(d.delay) || 10);
    var depth = Math.min(100, Math.max(1, parseFloat(d.scroll) || 50)) / 100;
    var days = Math.max(1, parseFloat(d.days) || 14);
    var design = d.design === 'true';
    var isOpen = false, subscribed = false, lastFocus = null, closeTimer = 0;

    function open(force) {
      if (isOpen) return;
      if (!force && (suppressed(days) || subscribed)) return;
      isOpen = true;
      clearTimeout(closeTimer);
      lastFocus = document.activeElement;
      if (typeof dlg.showModal === 'function') { try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); } }
      else dlg.setAttribute('open', '');
      root.classList.add('is-open');
      if (reduced()) dlg.classList.add('is-in');
      else requestAnimationFrame(function () { requestAnimationFrame(function () { dlg.classList.add('is-in'); }); });
      card.focus({ preventScroll: true });
      if (!design) window.addEventListener('pagehide', onLeave);
    }
    function finish() {
      if (dlg.open && typeof dlg.close === 'function') { try { dlg.close(); } catch (e) {} }
      dlg.removeAttribute('open');
      root.classList.remove('is-open');
      var back = lastFocus && document.contains(lastFocus) ? lastFocus : null;
      if (back && back.focus) back.focus({ preventScroll: true });
      lastFocus = null;
    }
    function close() {
      if (!isOpen) return;
      isOpen = false;
      window.removeEventListener('pagehide', onLeave);
      if (!subscribed && !design) writeState('dismissed:' + Date.now(), days);
      dlg.classList.remove('is-in');
      if (reduced()) finish(); else closeTimer = setTimeout(finish, 280);
    }
    function onLeave() { if (isOpen && !subscribed) writeState('dismissed:' + Date.now(), days); }

    root.querySelectorAll('[data-ph-pop-close]').forEach(function (b) { b.addEventListener('click', close); });
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); });         // Esc (native modal)
    dlg.addEventListener('close', function () { if (isOpen) { isOpen = false; finish(); if (!subscribed && !design) writeState('dismissed:' + Date.now(), days); } });
    dlg.addEventListener('click', function (e) { if (e.target === dlg) close(); });     // backdrop
    dlg.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables(card);
      if (!f.length) { e.preventDefault(); return; }
      var first = f[0], last = f[f.length - 1], a = document.activeElement;
      if (e.shiftKey && (a === first || a === card || !card.contains(a))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (a === last || !card.contains(a))) { e.preventDefault(); first.focus(); }
    });
    document.addEventListener('ph:subscribed', function () { subscribed = true; });

    root.__phOpen = function () { open(true); };
    root.__phClose = close;

    if (design) return;                                         // editor: opens on section select only
    if (BLOCKED.test(location.pathname) || suppressed(days)) return;
    try {
      if (/[?&](_kx|utm_medium=email)\b/i.test(location.search)) sessionStorage.setItem('ph_signup_skip', '1');
      if (sessionStorage.getItem('ph_signup_skip') === '1') return;   // arrived from one of our emails
    } catch (e) {}

    var armed = true, timer = 0;
    function trigger() {
      if (!armed) return;
      armed = false;
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      attempt();
    }
    var geo = (d.geo || '').toUpperCase();
    function attempt() {
      if (suppressed(days) || subscribed) return;              // e.g. signed up in the footer form meanwhile
      if (geo) {                                               // US only for real: Shopify geolocation, fails closed
        geoCountry().then(function (c) { if (c === geo) { geo = ''; attempt(); } });
        return;
      }
      if (document.hidden) { document.addEventListener('visibilitychange', function v() { document.removeEventListener('visibilitychange', v); attempt(); }); return; }
      if (otherLayerOpen(dlg) || typing(root)) { setTimeout(attempt, 4000); return; }   // never over the cart drawer / menu / a field in use
      open(false);
    }
    function onScroll() {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && (window.scrollY || window.pageYOffset) / h >= depth) trigger();
    }
    timer = setTimeout(trigger, delay * 1000);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function init() {
    // JS validates (custom messages); without JS the browser's own validation still applies.
    document.querySelectorAll('[data-ph-signup] form').forEach(function (f) { f.noValidate = true; });
    document.querySelectorAll('[data-ph-pop]').forEach(initPop);
  }

  // Theme editor: re-init after a section reload; open the pop-up while its section is selected.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', function (e) {
    var r = document.querySelector('[data-ph-pop][data-section-id="' + (e.detail && e.detail.sectionId) + '"]');
    if (r) { initPop(r); if (r.__phOpen) r.__phOpen(); }
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    var r = document.querySelector('[data-ph-pop][data-section-id="' + (e.detail && e.detail.sectionId) + '"]');
    if (r && r.__phClose) r.__phClose();
  });

  window.__phSignup = { init: init, payload: payload, readState: readState };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
